/* global */

class SplashCursor {
  constructor() {
    this.canvas = null;
    this.gl = null;
    this.ext = null;
    this.isRunning = false;
    // Match cursor breakpoint: >= 1199px (same as CSS breakpoint)
    this.isLargeDevice = window.innerWidth >= 1199;

    // Early exit for mobile/small viewports
    if (!this.isLargeDevice) {
      return; // Don't initialize splash cursor on small viewports
    }

    // Performance monitoring
    this.performance = {
      frameCount: 0,
      lastFpsTime: 0,
      fps: 60,
      targetFps: 60,
      qualityLevel: 1, // 0 = low, 1 = medium, 2 = high
      adaptiveQuality: true,
      frameTimeHistory: [],
      maxFrameTimeHistory: 30,
    };

    // Adaptive configuration based on device performance
    this.config = this.getAdaptiveConfig();
    this.pointers = [];
    this.programs = {};
    this.framebuffers = {};
    this.lastUpdateTime = Date.now();
    this.colorUpdateTimer = 0.0;

    // Cached elements for performance
    this.cachedElements = {
      blackSections: null,
      lastCacheTime: 0,
      cacheTimeout: 100, // 100ms cache for more responsive updates
    };

    // Throttled functions
    this.throttledUpdateMask = this.throttle(() => {
      if (this.isLargeDevice && this.canvas) {
        this.updateMask();
      }
    }, 16); // ~60fps for smoother updates
    this.throttledResize = this.throttle(this.handleResize.bind(this), 100);

    this.init();
  }

  getAdaptiveConfig() {
    const qualityLevels = {
      0: {
        // Low quality - for low-end devices
        SIM_RESOLUTION: 64,
        DYE_RESOLUTION: 512,
        CAPTURE_RESOLUTION: 128,
        DENSITY_DISSIPATION: 3.0,
        VELOCITY_DISSIPATION: 2.0,
        PRESSURE: 0.1,
        PRESSURE_ITERATIONS: 6,
        CURL: 4,
        SPLAT_RADIUS: 0.3,
        SPLAT_FORCE: 6000,
        SHADING: false,
        COLOR_UPDATE_SPEED: 10,
        PAUSED: false,
        BACK_COLOR: { r: 0.5, g: 0, b: 0 },
        TRANSPARENT: true,
      },
      1: {
        // Medium quality - default
        SIM_RESOLUTION: 96,
        DYE_RESOLUTION: 768,
        CAPTURE_RESOLUTION: 192,
        DENSITY_DISSIPATION: 2.8,
        VELOCITY_DISSIPATION: 1.8,
        PRESSURE: 0.15,
        PRESSURE_ITERATIONS: 8,
        CURL: 6,
        SPLAT_RADIUS: 0.35,
        SPLAT_FORCE: 7000,
        SHADING: false,
        COLOR_UPDATE_SPEED: 12,
        PAUSED: false,
        BACK_COLOR: { r: 0.5, g: 0, b: 0 },
        TRANSPARENT: true,
      },
      2: {
        // High quality - for high-end devices
        SIM_RESOLUTION: 128,
        DYE_RESOLUTION: 1024,
        CAPTURE_RESOLUTION: 256,
        DENSITY_DISSIPATION: 2.5,
        VELOCITY_DISSIPATION: 1.5,
        PRESSURE: 0.2,
        PRESSURE_ITERATIONS: 12,
        CURL: 8,
        SPLAT_RADIUS: 0.4,
        SPLAT_FORCE: 8000,
        SHADING: false,
        COLOR_UPDATE_SPEED: 15,
        PAUSED: false,
        BACK_COLOR: { r: 0.5, g: 0, b: 0 },
        TRANSPARENT: true,
      },
    };

    return qualityLevels[this.performance.qualityLevel];
  }

  // Utility function for throttling
  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  // Performance monitoring and adaptive quality
  updatePerformance(dt) {
    this.performance.frameCount++;
    const now = performance.now();

    if (now - this.performance.lastFpsTime >= 1000) {
      this.performance.fps = this.performance.frameCount;
      this.performance.frameCount = 0;
      this.performance.lastFpsTime = now;

      // Adaptive quality adjustment
      if (this.performance.adaptiveQuality) {
        this.adjustQuality();
      }
    }

    // Track frame times
    this.performance.frameTimeHistory.push(dt * 1000);
    if (
      this.performance.frameTimeHistory.length >
      this.performance.maxFrameTimeHistory
    ) {
      this.performance.frameTimeHistory.shift();
    }
  }

  adjustQuality() {
    const avgFrameTime =
      this.performance.frameTimeHistory.reduce((a, b) => a + b, 0) /
      this.performance.frameTimeHistory.length;
    const targetFrameTime = 1000 / this.performance.targetFps;

    if (
      avgFrameTime > targetFrameTime * 1.2 &&
      this.performance.qualityLevel > 0
    ) {
      // Performance is poor, reduce quality
      this.performance.qualityLevel--;
      this.config = this.getAdaptiveConfig();
      this.reinitializeFramebuffers();
    } else if (
      avgFrameTime < targetFrameTime * 0.8 &&
      this.performance.qualityLevel < 2
    ) {
      // Performance is good, increase quality
      this.performance.qualityLevel++;
      this.config = this.getAdaptiveConfig();
      this.reinitializeFramebuffers();
    }
  }

  reinitializeFramebuffers() {
    if (this.gl && this.framebuffers) {
      this.initFramebuffers();
    }
  }

  init() {
    // Only initialize on large devices (≥1200px)
    if (!this.isLargeDevice) {
      this.setupResizeHandler();
      return;
    }

    // Detect device performance and adjust initial quality
    this.detectDevicePerformance();

    // Initialize on load
    if (document.readyState === "loading") {
      window.addEventListener("load", () => {
        // Double-check device size before initializing
        if (window.innerWidth >= 1200) {
          this.initFluid();
        }
      });
    } else {
      // Double-check device size before initializing
      if (window.innerWidth >= 1200) {
        this.initFluid();
      }
    }

    this.setupResizeHandler();
  }

  detectDevicePerformance() {
    // Simple performance detection based on hardware concurrency and memory
    const cores = navigator.hardwareConcurrency || 4;
    const memory = navigator.deviceMemory || 4; // GB
    const isLowEnd = cores <= 2 || memory <= 2;

    if (isLowEnd) {
      this.performance.qualityLevel = 0; // Start with low quality
      this.performance.targetFps = 30; // Target 30fps for low-end devices
    } else {
      this.performance.qualityLevel = 1; // Start with medium quality
      this.performance.targetFps = 60; // Target 60fps for better devices
    }

    // Update config based on detected performance
    this.config = this.getAdaptiveConfig();
  }

  initFluid() {
    this.createCanvas();
    this.setupPointers();
    this.resizeCanvas();
    this.setupWebGL();
    this.setupShaders();
    this.initFramebuffers();
    this.setupEventListeners();
    this.update();
  }

  createCanvas() {
    // Create single canvas covering entire viewport
    this.canvas = document.createElement("canvas");
    this.canvas.id = "fluid";
    this.canvas.className = "fluid-canvas";
    this.canvas.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 0;
      opacity: 1;
      will-change: contents;
      transform: translateZ(0);
      mix-blend-mode: normal;
      display: none;
    `;

    // Set initial size immediately
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;

    document.body.appendChild(this.canvas);
    this.createMask();
  }

  createMask() {
    // Don't create mask on mobile viewports
    if (!this.isLargeDevice || !this.canvas) return;

    // Create a mask that only shows fluid within black sections
    this.updateMask();

    // Update mask when window resizes (throttled)
    window.addEventListener("resize", this.throttledUpdateMask);

    // Update mask on scroll to handle dynamic content changes
    let lastScrollY = window.scrollY;
    const scrollHandler = () => {
      const currentScrollY = window.scrollY;
      const scrollDelta = Math.abs(currentScrollY - lastScrollY);

      // Only update if scroll delta is significant
      if (scrollDelta > 10) {
        this.throttledUpdateMask();
        lastScrollY = currentScrollY;
      }
    };
    window.addEventListener("scroll", scrollHandler, { passive: true });

    // Update mask periodically to catch dynamic content changes (less frequent)
    setInterval(() => this.throttledUpdateMask(), 2000);
  }

  updateMask() {
    // Don't update mask if canvas doesn't exist (mobile viewports)
    if (!this.canvas) return;

    const now = performance.now();

    // Use cached elements if available and not expired
    let blackSections = this.cachedElements.blackSections;
    if (
      !blackSections ||
      now - this.cachedElements.lastCacheTime > this.cachedElements.cacheTimeout
    ) {
      blackSections = document.querySelectorAll(".section--black");
      this.cachedElements.blackSections = blackSections;
      this.cachedElements.lastCacheTime = now;
    }

    if (!blackSections.length) {
      // If no black sections, hide the canvas completely
      if (this.canvas) {
        this.canvas.style.display = "none";
      }
      return;
    }

    // Check if any black sections are currently visible
    const visibleSections = Array.from(blackSections).filter((section) => {
      const rect = section.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top < window.innerHeight &&
        rect.bottom > 0
      );
    });

    if (visibleSections.length === 0) {
      // If no visible black sections, hide the canvas
      if (this.canvas) {
        this.canvas.style.display = "none";
      }
      return;
    }

    // Create SVG mask
    let maskSVG = document.getElementById("fluid-mask");
    if (!maskSVG) {
      maskSVG = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      maskSVG.id = "fluid-mask";
      maskSVG.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        pointer-events: none;
        z-index: -1;
        opacity: 0;
      `;
      document.body.appendChild(maskSVG);
    }

    // Clear existing mask content
    maskSVG.innerHTML = "";

    // Create mask definition
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const mask = document.createElementNS("http://www.w3.org/2000/svg", "mask");
    mask.id = "blackSectionMask";

    // Black background (hides everything)
    const blackRect = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "rect",
    );
    blackRect.setAttribute("width", "100%");
    blackRect.setAttribute("height", "100%");
    blackRect.setAttribute("fill", "black");
    mask.appendChild(blackRect);

    // White rectangles for each visible black section (shows fluid)
    // Process synchronously to avoid black areas during mask update
    visibleSections.forEach((section) => {
      const rect = section.getBoundingClientRect();

      const whiteRect = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "rect",
      );
      whiteRect.setAttribute("x", rect.left);
      whiteRect.setAttribute("y", rect.top);
      whiteRect.setAttribute("width", rect.width);
      whiteRect.setAttribute("height", rect.height);
      whiteRect.setAttribute("fill", "white");
      mask.appendChild(whiteRect);
    });

    defs.appendChild(mask);
    maskSVG.appendChild(defs);

    // Apply mask to canvas (with null check)
    if (this.canvas && this.canvas.style) {
      // Pre-apply mask properties to prevent black areas
      this.canvas.style.mask = "url(#blackSectionMask)";
      this.canvas.style.webkitMask = "url(#blackSectionMask)";
      this.canvas.style.maskSize = "100% 100%";
      this.canvas.style.webkitMaskSize = "100% 100%";
      this.canvas.style.maskRepeat = "no-repeat";
      this.canvas.style.webkitMaskRepeat = "no-repeat";

      // Show canvas after mask is applied
      this.canvas.style.display = "block";
    }
  }

  setupPointers() {
    const pointerPrototype = function () {
      this.id = -1;
      this.texcoordX = 0;
      this.texcoordY = 0;
      this.prevTexcoordX = 0;
      this.prevTexcoordY = 0;
      this.deltaX = 0;
      this.deltaY = 0;
      this.down = false;
      this.moved = false;
      this.color = [0, 0, 0];
    };

    this.pointers.push(new pointerPrototype());
  }

  resizeCanvas() {
    let width = this.scaleByPixelRatio(this.canvas.clientWidth);
    let height = this.scaleByPixelRatio(this.canvas.clientHeight);
    if (this.canvas.width != width || this.canvas.height != height) {
      this.canvas.width = width;
      this.canvas.height = height;
      return true;
    }
    return false;
  }

  setupWebGL() {
    const context = this.getWebGLContext(this.canvas);
    if (!context) {
      this.destroy();
      return;
    }

    const { gl, ext } = context;
    this.gl = gl;
    this.ext = ext;

    if (!ext.supportLinearFiltering) {
      this.config.DYE_RESOLUTION = 512;
      this.config.SHADING = false;
    }
  }

  getWebGLContext(canvas) {
    const params = {
      alpha: true,
      depth: false,
      stencil: false,
      antialias: false,
      preserveDrawingBuffer: false,
      powerPreference: "high-performance", // Prefer discrete GPU
    };

    let gl = canvas.getContext("webgl2", params);
    const isWebGL2 = !!gl;
    if (!isWebGL2) {
      gl =
        canvas.getContext("webgl", params) ||
        canvas.getContext("experimental-webgl", params);
    }

    // Fallback for older browsers
    if (!gl) {
      return null;
    }

    let halfFloat;
    let supportLinearFiltering;
    if (isWebGL2) {
      gl.getExtension("EXT_color_buffer_float");
      supportLinearFiltering = gl.getExtension("OES_texture_float_linear");
    } else {
      halfFloat = gl.getExtension("OES_texture_half_float");
      supportLinearFiltering = gl.getExtension("OES_texture_half_float_linear");
    }

    gl.clearColor(0.0, 0.0, 0.0, 1.0);

    const halfFloatTexType = isWebGL2
      ? gl.HALF_FLOAT
      : halfFloat.HALF_FLOAT_OES;
    let formatRGBA, formatRG, formatR;

    if (isWebGL2) {
      formatRGBA = this.getSupportedFormat(
        gl,
        gl.RGBA16F,
        gl.RGBA,
        halfFloatTexType,
      );
      formatRG = this.getSupportedFormat(gl, gl.RG16F, gl.RG, halfFloatTexType);
      formatR = this.getSupportedFormat(gl, gl.R16F, gl.RED, halfFloatTexType);
    } else {
      formatRGBA = this.getSupportedFormat(
        gl,
        gl.RGBA,
        gl.RGBA,
        halfFloatTexType,
      );
      formatRG = this.getSupportedFormat(
        gl,
        gl.RGBA,
        gl.RGBA,
        halfFloatTexType,
      );
      formatR = this.getSupportedFormat(gl, gl.RGBA, gl.RGBA, halfFloatTexType);
    }

    return {
      gl,
      ext: {
        formatRGBA,
        formatRG,
        formatR,
        halfFloatTexType,
        supportLinearFiltering,
      },
    };
  }

  getSupportedFormat(gl, internalFormat, format, type) {
    if (!this.supportRenderTextureFormat(gl, internalFormat, format, type)) {
      switch (internalFormat) {
        case gl.R16F:
          return this.getSupportedFormat(gl, gl.RG16F, gl.RG, type);
        case gl.RG16F:
          return this.getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, type);
        default:
          return null;
      }
    }
    return { internalFormat, format };
  }

  supportRenderTextureFormat(gl, internalFormat, format, type) {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      internalFormat,
      4,
      4,
      0,
      format,
      type,
      null,
    );

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    );

    const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
    return status === gl.FRAMEBUFFER_COMPLETE;
  }

  setupShaders() {
    const gl = this.gl;

    // Base vertex shader
    const baseVertexShader = this.compileShader(
      gl.VERTEX_SHADER,
      `
      precision highp float;
      attribute vec2 aPosition;
      varying vec2 vUv;
      varying vec2 vL;
      varying vec2 vR;
      varying vec2 vT;
      varying vec2 vB;
      uniform vec2 texelSize;
      void main () {
          vUv = aPosition * 0.5 + 0.5;
          vL = vUv - vec2(texelSize.x, 0.0);
          vR = vUv + vec2(texelSize.x, 0.0);
          vT = vUv + vec2(0.0, texelSize.y);
          vB = vUv - vec2(0.0, texelSize.y);
          gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `,
    );

    // Display fragment shader
    const displayShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision highp float;
      precision highp sampler2D;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      void main () {
          vec3 c = texture2D(uTexture, vUv).rgb;
          float a = max(c.r, max(c.g, c.b));
          gl_FragColor = vec4(c, a);
      }
    `,
    );

    // Splat fragment shader
    const splatShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision highp float;
      precision highp sampler2D;
      varying vec2 vUv;
      uniform sampler2D uTarget;
      uniform float aspectRatio;
      uniform vec3 color;
      uniform vec2 point;
      uniform float radius;
      void main () {
          vec2 p = vUv - point.xy;
          p.x *= aspectRatio;
          vec3 splat = exp(-dot(p, p) / radius) * color;
          vec3 base = texture2D(uTarget, vUv).xyz;
          gl_FragColor = vec4(base + splat, 1.0);
      }
    `,
    );

    // Advection fragment shader
    const advectionShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision highp float;
      precision highp sampler2D;
      varying vec2 vUv;
      uniform sampler2D uVelocity;
      uniform sampler2D uSource;
      uniform vec2 texelSize;
      uniform vec2 dyeTexelSize;
      uniform float dt;
      uniform float dissipation;
      void main () {
          vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
          vec4 result = texture2D(uSource, coord);
          float decay = 1.0 + dissipation * dt;
          gl_FragColor = result / decay;
      }
    `,
    );

    // Divergence shader
    const divergenceShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision mediump float;
      precision mediump sampler2D;
      varying highp vec2 vUv;
      varying highp vec2 vL;
      varying highp vec2 vR;
      varying highp vec2 vT;
      varying highp vec2 vB;
      uniform sampler2D uVelocity;
      void main () {
          float L = texture2D(uVelocity, vL).x;
          float R = texture2D(uVelocity, vR).x;
          float T = texture2D(uVelocity, vT).y;
          float B = texture2D(uVelocity, vB).y;
          vec2 C = texture2D(uVelocity, vUv).xy;
          if (vL.x < 0.0) { L = -C.x; }
          if (vR.x > 1.0) { R = -C.x; }
          if (vT.y > 1.0) { T = -C.y; }
          if (vB.y < 0.0) { B = -C.y; }
          float div = 0.5 * (R - L + T - B);
          gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
      }
    `,
    );

    // Curl shader
    const curlShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision mediump float;
      precision mediump sampler2D;
      varying highp vec2 vUv;
      varying highp vec2 vL;
      varying highp vec2 vR;
      varying highp vec2 vT;
      varying highp vec2 vB;
      uniform sampler2D uVelocity;
      void main () {
          float L = texture2D(uVelocity, vL).y;
          float R = texture2D(uVelocity, vR).y;
          float T = texture2D(uVelocity, vT).x;
          float B = texture2D(uVelocity, vB).x;
          float vorticity = R - L - T + B;
          gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
      }
    `,
    );

    // Vorticity shader
    const vorticityShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision highp float;
      precision highp sampler2D;
      varying vec2 vUv;
      varying vec2 vL;
      varying vec2 vR;
      varying vec2 vT;
      varying vec2 vB;
      uniform sampler2D uVelocity;
      uniform sampler2D uCurl;
      uniform float curl;
      uniform float dt;
      void main () {
          float L = texture2D(uCurl, vL).x;
          float R = texture2D(uCurl, vR).x;
          float T = texture2D(uCurl, vT).x;
          float B = texture2D(uCurl, vB).x;
          float C = texture2D(uCurl, vUv).x;
          vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
          force /= length(force) + 0.0001;
          force *= curl * C;
          force.y *= -1.0;
          vec2 velocity = texture2D(uVelocity, vUv).xy;
          velocity += force * dt;
          velocity = min(max(velocity, -1000.0), 1000.0);
          gl_FragColor = vec4(velocity, 0.0, 1.0);
      }
    `,
    );

    // Pressure shader
    const pressureShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision mediump float;
      precision mediump sampler2D;
      varying highp vec2 vUv;
      varying highp vec2 vL;
      varying highp vec2 vR;
      varying highp vec2 vT;
      varying highp vec2 vB;
      uniform sampler2D uPressure;
      uniform sampler2D uDivergence;
      void main () {
          float L = texture2D(uPressure, vL).x;
          float R = texture2D(uPressure, vR).x;
          float T = texture2D(uPressure, vT).x;
          float B = texture2D(uPressure, vB).x;
          float C = texture2D(uPressure, vUv).x;
          float divergence = texture2D(uDivergence, vUv).x;
          float pressure = (L + R + B + T - divergence) * 0.25;
          gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
      }
    `,
    );

    // Gradient subtract shader
    const gradientSubtractShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision mediump float;
      precision mediump sampler2D;
      varying highp vec2 vUv;
      varying highp vec2 vL;
      varying highp vec2 vR;
      varying highp vec2 vT;
      varying highp vec2 vB;
      uniform sampler2D uPressure;
      uniform sampler2D uVelocity;
      void main () {
          float L = texture2D(uPressure, vL).x;
          float R = texture2D(uPressure, vR).x;
          float T = texture2D(uPressure, vT).x;
          float B = texture2D(uPressure, vB).x;
          vec2 velocity = texture2D(uVelocity, vUv).xy;
          velocity.xy -= vec2(R - L, T - B);
          gl_FragColor = vec4(velocity, 0.0, 1.0);
      }
    `,
    );

    // Clear shader
    const clearShader = this.compileShader(
      gl.FRAGMENT_SHADER,
      `
      precision mediump float;
      precision mediump sampler2D;
      varying highp vec2 vUv;
      uniform sampler2D uTexture;
      uniform float value;
      void main () {
          gl_FragColor = value * texture2D(uTexture, vUv);
      }
    `,
    );

    // Create programs
    this.programs.display = this.createProgram(baseVertexShader, displayShader);
    this.programs.splat = this.createProgram(baseVertexShader, splatShader);
    this.programs.advection = this.createProgram(
      baseVertexShader,
      advectionShader,
    );
    this.programs.divergence = this.createProgram(
      baseVertexShader,
      divergenceShader,
    );
    this.programs.curl = this.createProgram(baseVertexShader, curlShader);
    this.programs.vorticity = this.createProgram(
      baseVertexShader,
      vorticityShader,
    );
    this.programs.pressure = this.createProgram(
      baseVertexShader,
      pressureShader,
    );
    this.programs.gradientSubtract = this.createProgram(
      baseVertexShader,
      gradientSubtractShader,
    );
    this.programs.clear = this.createProgram(baseVertexShader, clearShader);

    // Setup blit
    this.setupBlit();
  }

  compileShader(type, source) {
    const gl = this.gl;
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
    }

    return shader;
  }

  createProgram(vertexShader, fragmentShader) {
    const gl = this.gl;
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
    }

    return {
      program,
      uniforms: this.getUniforms(program),
    };
  }

  getUniforms(program) {
    const gl = this.gl;
    const uniforms = {};
    const uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < uniformCount; i++) {
      const uniformName = gl.getActiveUniform(program, i).name;
      uniforms[uniformName] = gl.getUniformLocation(program, uniformName);
    }
    return uniforms;
  }

  setupBlit() {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
      gl.STATIC_DRAW,
    );
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array([0, 1, 2, 0, 2, 3]),
      gl.STATIC_DRAW,
    );
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);

    this.blit = (target) => {
      if (target == null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      } else {
        gl.viewport(0, 0, target.width, target.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
      }
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    };
  }

  initFramebuffers() {
    const simRes = this.getResolution(this.config.SIM_RESOLUTION);
    const dyeRes = this.getResolution(this.config.DYE_RESOLUTION);

    const texType = this.ext.halfFloatTexType;
    const rgba = this.ext.formatRGBA;
    const rg = this.ext.formatRG;
    const r = this.ext.formatR;
    const filtering = this.ext.supportLinearFiltering
      ? this.gl.LINEAR
      : this.gl.NEAREST;

    this.gl.disable(this.gl.BLEND);

    this.framebuffers.dye = this.createDoubleFBO(
      dyeRes.width,
      dyeRes.height,
      rgba.internalFormat,
      rgba.format,
      texType,
      filtering,
    );
    this.framebuffers.velocity = this.createDoubleFBO(
      simRes.width,
      simRes.height,
      rg.internalFormat,
      rg.format,
      texType,
      filtering,
    );
    this.framebuffers.divergence = this.createFBO(
      simRes.width,
      simRes.height,
      r.internalFormat,
      r.format,
      texType,
      this.gl.NEAREST,
    );
    this.framebuffers.curl = this.createFBO(
      simRes.width,
      simRes.height,
      r.internalFormat,
      r.format,
      texType,
      this.gl.NEAREST,
    );
    this.framebuffers.pressure = this.createDoubleFBO(
      simRes.width,
      simRes.height,
      r.internalFormat,
      r.format,
      texType,
      this.gl.NEAREST,
    );
  }

  createFBO(w, h, internalFormat, format, type, param) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      internalFormat,
      w,
      h,
      0,
      format,
      type,
      null,
    );

    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      texture,
      0,
    );
    gl.viewport(0, 0, w, h);
    gl.clear(gl.COLOR_BUFFER_BIT);

    return {
      texture,
      fbo,
      width: w,
      height: h,
      texelSizeX: 1.0 / w,
      texelSizeY: 1.0 / h,
      attach(id) {
        gl.activeTexture(gl.TEXTURE0 + id);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        return id;
      },
    };
  }

  createDoubleFBO(w, h, internalFormat, format, type, param) {
    let fbo1 = this.createFBO(w, h, internalFormat, format, type, param);
    let fbo2 = this.createFBO(w, h, internalFormat, format, type, param);

    return {
      width: w,
      height: h,
      texelSizeX: fbo1.texelSizeX,
      texelSizeY: fbo1.texelSizeY,
      get read() {
        return fbo1;
      },
      set read(value) {
        fbo1 = value;
      },
      get write() {
        return fbo2;
      },
      set write(value) {
        fbo2 = value;
      },
      swap() {
        const temp = fbo1;
        fbo1 = fbo2;
        fbo2 = temp;
      },
    };
  }

  setupEventListeners() {
    // Don't setup event listeners on mobile viewports
    if (!this.isLargeDevice || !this.canvas) return;

    let isFirstMove = true;
    let isOverBlackSection = false;
    let lastMoveTime = 0;
    let lastCheckTime = 0;

    // Adaptive throttling based on performance
    const getMoveThrottle = () => {
      const baseThrottle = this.performance.qualityLevel === 0 ? 16 : 8; // 60fps for low quality, 120fps for others
      return Math.max(baseThrottle, 1000 / this.performance.targetFps);
    };

    // Optimized mouse move handler
    const handleMouseMove = (e) => {
      const now = performance.now();
      const moveThrottle = getMoveThrottle();

      if (now - lastMoveTime < moveThrottle) return;
      lastMoveTime = now;

      // Use cached black sections for performance
      const now2 = performance.now();
      if (now2 - lastCheckTime > 50) {
        // Check every 50ms for more responsive updates
        lastCheckTime = now2;
        const elementUnderMouse = document.elementFromPoint(
          e.clientX,
          e.clientY,
        );
        const wasOverBlackSection = isOverBlackSection;
        isOverBlackSection =
          elementUnderMouse?.closest(".section--black") !== null;

        // If we just entered a black section, update mask immediately
        if (!wasOverBlackSection && isOverBlackSection) {
          this.updateMask();
        }
      }

      if (!isOverBlackSection) return;

      const pointer = this.pointers[0];
      // Safety check: ensure pointer exists and is initialized
      if (!pointer) return;

      const posX = this.scaleByPixelRatio(e.clientX);
      const posY = this.scaleByPixelRatio(e.clientY);

      if (isFirstMove) {
        isFirstMove = false;
        const color = this.generateColor();
        this.updatePointerMoveData(pointer, posX, posY, color);
        this.splatPointer(pointer);
      } else {
        // Safety check: ensure pointer.color exists, otherwise generate a new color
        const color = pointer.color || this.generateColor();
        this.updatePointerMoveData(pointer, posX, posY, color);
      }
    };

    // Mouse events
    window.addEventListener("mousemove", handleMouseMove, { passive: true });

    window.addEventListener(
      "mouseleave",
      () => {
        isFirstMove = true;
        isOverBlackSection = false;
      },
      { passive: true },
    );

    // Optimized mouse down handler
    const handleMouseDown = (e) => {
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY);
      const blackSection = elementUnderMouse?.closest(".section--black");

      if (blackSection) {
        const pointer = this.pointers[0];
        if (!pointer) return;
        const posX = this.scaleByPixelRatio(e.clientX);
        const posY = this.scaleByPixelRatio(e.clientY);
        this.updatePointerDownData(pointer, -1, posX, posY);
        this.clickSplat(pointer);
      }
    };

    window.addEventListener("mousedown", handleMouseDown, { passive: true });

    // Touch events with optimization
    let isFirstTouch = true;

    const handleTouchStart = (e) => {
      const touches = e.targetTouches;
      const touch = touches[0];
      const elementUnderMouse = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      );
      const blackSection = elementUnderMouse?.closest(".section--black");

      if (blackSection) {
        if (isFirstTouch) {
          isFirstTouch = false;
          this.update();
        }

        const pointer = this.pointers[0];
        if (!pointer) return;
        const posX = this.scaleByPixelRatio(touch.clientX);
        const posY = this.scaleByPixelRatio(touch.clientY);
        this.updatePointerDownData(pointer, touch.identifier, posX, posY);
      }
    };

    const handleTouchMove = (e) => {
      const touches = e.targetTouches;
      const touch = touches[0];
      const elementUnderMouse = document.elementFromPoint(
        touch.clientX,
        touch.clientY,
      );
      const blackSection = elementUnderMouse?.closest(".section--black");

      if (blackSection) {
        const pointer = this.pointers[0];
        if (!pointer) return;
        const posX = this.scaleByPixelRatio(touch.clientX);
        const posY = this.scaleByPixelRatio(touch.clientY);
        const color = pointer.color || this.generateColor();
        this.updatePointerMoveData(pointer, posX, posY, color);
      }
    };

    const handleTouchEnd = () => {
      const pointer = this.pointers[0];
      if (pointer) {
        this.updatePointerUpData(pointer);
      }
    };

    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
  }

  update() {
    // Don't update on mobile viewports or if canvas doesn't exist
    if (!this.isLargeDevice || !this.canvas) return;

    if (!this.isRunning) {
      this.isRunning = true;
      this.lastUpdateTime = performance.now();
    }

    const dt = this.calcDeltaTime();

    // Update performance monitoring
    this.updatePerformance(dt);

    // Skip frame if performance is too poor
    if (this.performance.qualityLevel === 0 && dt > 0.033) {
      // Skip if > 30fps on low quality
      requestAnimationFrame(() => this.update());
      return;
    }

    if (this.resizeCanvas()) {
      this.initFramebuffers();
    }
    this.updateColors(dt);
    this.applyInputs();
    this.step(dt);
    this.render();
    requestAnimationFrame(() => this.update());
  }

  calcDeltaTime() {
    const now = performance.now();
    let dt = (now - this.lastUpdateTime) / 1000;

    // Adaptive frame rate capping based on quality level
    const maxDt = this.performance.qualityLevel === 0 ? 0.033 : 0.016666; // 30fps for low, 60fps for others
    const minDt = this.performance.qualityLevel === 0 ? 0.016666 : 0.008333; // 60fps min for low, 120fps for others

    dt = Math.min(dt, maxDt);
    dt = Math.max(dt, minDt);
    this.lastUpdateTime = now;
    return dt;
  }

  updateColors(dt) {
    this.colorUpdateTimer += dt * this.config.COLOR_UPDATE_SPEED;
    if (this.colorUpdateTimer >= 1) {
      this.colorUpdateTimer = this.wrap(this.colorUpdateTimer, 0, 1);
      this.pointers.forEach((p) => {
        p.color = this.generateColor();
      });
    }
  }

  applyInputs() {
    this.pointers.forEach((p) => {
      if (p.moved) {
        p.moved = false;
        this.splatPointer(p);
      }
    });
  }

  step(dt) {
    const gl = this.gl;
    gl.disable(gl.BLEND);

    // Skip some calculations on low quality for better performance
    const skipVorticity = this.performance.qualityLevel === 0;
    const skipPressure = this.performance.qualityLevel === 0;

    // Curl calculation
    const curl = this.programs.curl;
    gl.useProgram(curl.program);
    gl.uniform2f(
      curl.uniforms.texelSize,
      this.framebuffers.velocity.texelSizeX,
      this.framebuffers.velocity.texelSizeY,
    );
    gl.uniform1i(
      curl.uniforms.uVelocity,
      this.framebuffers.velocity.read.attach(0),
    );
    this.blit(this.framebuffers.curl);

    // Vorticity (skip on low quality)
    if (!skipVorticity) {
      const vorticity = this.programs.vorticity;
      gl.useProgram(vorticity.program);
      gl.uniform2f(
        vorticity.uniforms.texelSize,
        this.framebuffers.velocity.texelSizeX,
        this.framebuffers.velocity.texelSizeY,
      );
      gl.uniform1i(
        vorticity.uniforms.uVelocity,
        this.framebuffers.velocity.read.attach(0),
      );
      gl.uniform1i(vorticity.uniforms.uCurl, this.framebuffers.curl.attach(1));
      gl.uniform1f(vorticity.uniforms.curl, this.config.CURL);
      gl.uniform1f(vorticity.uniforms.dt, dt);
      this.blit(this.framebuffers.velocity.write);
      this.framebuffers.velocity.swap();
    }

    // Divergence
    const divergence = this.programs.divergence;
    gl.useProgram(divergence.program);
    gl.uniform2f(
      divergence.uniforms.texelSize,
      this.framebuffers.velocity.texelSizeX,
      this.framebuffers.velocity.texelSizeY,
    );
    gl.uniform1i(
      divergence.uniforms.uVelocity,
      this.framebuffers.velocity.read.attach(0),
    );
    this.blit(this.framebuffers.divergence);

    // Pressure solve (simplified on low quality)
    if (!skipPressure) {
      // Clear pressure
      const clear = this.programs.clear;
      gl.useProgram(clear.program);
      gl.uniform1i(
        clear.uniforms.uTexture,
        this.framebuffers.pressure.read.attach(0),
      );
      gl.uniform1f(clear.uniforms.value, this.config.PRESSURE);
      this.blit(this.framebuffers.pressure.write);
      this.framebuffers.pressure.swap();

      // Pressure solve
      const pressure = this.programs.pressure;
      gl.useProgram(pressure.program);
      gl.uniform2f(
        pressure.uniforms.texelSize,
        this.framebuffers.velocity.texelSizeX,
        this.framebuffers.velocity.texelSizeY,
      );
      gl.uniform1i(
        pressure.uniforms.uDivergence,
        this.framebuffers.divergence.attach(0),
      );
      for (let i = 0; i < this.config.PRESSURE_ITERATIONS; i++) {
        gl.uniform1i(
          pressure.uniforms.uPressure,
          this.framebuffers.pressure.read.attach(1),
        );
        this.blit(this.framebuffers.pressure.write);
        this.framebuffers.pressure.swap();
      }

      // Gradient subtract
      const gradientSubtract = this.programs.gradientSubtract;
      gl.useProgram(gradientSubtract.program);
      gl.uniform2f(
        gradientSubtract.uniforms.texelSize,
        this.framebuffers.velocity.texelSizeX,
        this.framebuffers.velocity.texelSizeY,
      );
      gl.uniform1i(
        gradientSubtract.uniforms.uPressure,
        this.framebuffers.pressure.read.attach(0),
      );
      gl.uniform1i(
        gradientSubtract.uniforms.uVelocity,
        this.framebuffers.velocity.read.attach(1),
      );
      this.blit(this.framebuffers.velocity.write);
      this.framebuffers.velocity.swap();
    }

    // Velocity advection
    const advection = this.programs.advection;
    gl.useProgram(advection.program);
    gl.uniform2f(
      advection.uniforms.texelSize,
      this.framebuffers.velocity.texelSizeX,
      this.framebuffers.velocity.texelSizeY,
    );
    if (!this.ext.supportLinearFiltering) {
      gl.uniform2f(
        advection.uniforms.dyeTexelSize,
        this.framebuffers.velocity.texelSizeX,
        this.framebuffers.velocity.texelSizeY,
      );
    }
    const velocityId = this.framebuffers.velocity.read.attach(0);
    gl.uniform1i(advection.uniforms.uVelocity, velocityId);
    gl.uniform1i(advection.uniforms.uSource, velocityId);
    gl.uniform1f(advection.uniforms.dt, dt);
    gl.uniform1f(
      advection.uniforms.dissipation,
      this.config.VELOCITY_DISSIPATION,
    );
    this.blit(this.framebuffers.velocity.write);
    this.framebuffers.velocity.swap();

    // Dye advection
    if (!this.ext.supportLinearFiltering) {
      gl.uniform2f(
        advection.uniforms.dyeTexelSize,
        this.framebuffers.dye.texelSizeX,
        this.framebuffers.dye.texelSizeY,
      );
    }
    gl.uniform1i(
      advection.uniforms.uVelocity,
      this.framebuffers.velocity.read.attach(0),
    );
    gl.uniform1i(
      advection.uniforms.uSource,
      this.framebuffers.dye.read.attach(1),
    );
    gl.uniform1f(
      advection.uniforms.dissipation,
      this.config.DENSITY_DISSIPATION,
    );
    this.blit(this.framebuffers.dye.write);
    this.framebuffers.dye.swap();
  }

  render() {
    const gl = this.gl;
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.BLEND);

    const display = this.programs.display;
    gl.useProgram(display.program);
    gl.uniform1i(
      display.uniforms.uTexture,
      this.framebuffers.dye.read.attach(0),
    );
    this.blit(null);
  }

  splatPointer(pointer) {
    const dx = pointer.deltaX * this.config.SPLAT_FORCE;
    const dy = pointer.deltaY * this.config.SPLAT_FORCE;
    this.splat(pointer.texcoordX, pointer.texcoordY, dx, dy, pointer.color);
  }

  clickSplat(pointer) {
    const color = this.generateColor();
    color.r *= 10.0;
    color.g *= 10.0;
    color.b *= 10.0;
    const dx = 10 * (Math.random() - 0.5);
    const dy = 30 * (Math.random() - 0.5);
    this.splat(pointer.texcoordX, pointer.texcoordY, dx, dy, color);
  }

  splat(x, y, dx, dy, color) {
    const gl = this.gl;
    const program = this.programs.splat;

    gl.useProgram(program.program);
    gl.uniform1i(
      program.uniforms.uTarget,
      this.framebuffers.velocity.read.attach(0),
    );
    gl.uniform1f(
      program.uniforms.aspectRatio,
      this.canvas.width / this.canvas.height,
    );
    gl.uniform2f(program.uniforms.point, x, y);
    gl.uniform3f(program.uniforms.color, dx, dy, 0.0);
    gl.uniform1f(
      program.uniforms.radius,
      this.correctRadius(this.config.SPLAT_RADIUS / 100.0),
    );
    this.blit(this.framebuffers.velocity.write);
    this.framebuffers.velocity.swap();

    gl.uniform1i(
      program.uniforms.uTarget,
      this.framebuffers.dye.read.attach(0),
    );
    gl.uniform3f(program.uniforms.color, color.r, color.g, color.b);
    this.blit(this.framebuffers.dye.write);
    this.framebuffers.dye.swap();
  }

  correctRadius(radius) {
    const aspectRatio = this.canvas.width / this.canvas.height;
    if (aspectRatio > 1) radius *= aspectRatio;
    return radius;
  }

  updatePointerDownData(pointer, id, posX, posY) {
    pointer.id = id;
    pointer.down = true;
    pointer.moved = false;
    pointer.texcoordX = posX / this.canvas.width;
    pointer.texcoordY = 1.0 - posY / this.canvas.height;
    pointer.prevTexcoordX = pointer.texcoordX;
    pointer.prevTexcoordY = pointer.texcoordY;
    pointer.deltaX = 0;
    pointer.deltaY = 0;
    pointer.color = this.generateColor();
  }

  updatePointerMoveData(pointer, posX, posY, color) {
    pointer.prevTexcoordX = pointer.texcoordX;
    pointer.prevTexcoordY = pointer.texcoordY;
    pointer.texcoordX = posX / this.canvas.width;
    pointer.texcoordY = 1.0 - posY / this.canvas.height;
    pointer.deltaX = this.correctDeltaX(
      pointer.texcoordX - pointer.prevTexcoordX,
    );
    pointer.deltaY = this.correctDeltaY(
      pointer.texcoordY - pointer.prevTexcoordY,
    );
    pointer.moved =
      Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0;
    pointer.color = color;
  }

  updatePointerUpData(pointer) {
    pointer.down = false;
  }

  correctDeltaX(delta) {
    const aspectRatio = this.canvas.width / this.canvas.height;
    if (aspectRatio < 1) delta *= aspectRatio;
    return delta;
  }

  correctDeltaY(delta) {
    const aspectRatio = this.canvas.width / this.canvas.height;
    if (aspectRatio > 1) delta /= aspectRatio;
    return delta;
  }

  generateColor() {
    const c = this.HSVtoRGB(Math.random(), 1.0, 1.0);
    c.r *= 0.25; // Increased intensity for better visibility
    c.g *= 0.25;
    c.b *= 0.25;
    return c;
  }

  // Alternative single color generation like original
  generateSingleColor() {
    // Base color #c5fcfc in RGB
    const baseR = 0xc5 / 255;
    const baseG = 0xfc / 255;
    const baseB = 0xfc / 255;

    // Generate a random shade (0 to 1)
    const shade = Math.random();

    // Apply the shade to the base color
    let r = baseR * shade;
    let g = baseG * shade;
    let b = baseB * shade;

    // Adjust the brightness
    r *= 0.15;
    g *= 0.15;
    b *= 0.15;

    return { r, g, b };
  }

  HSVtoRGB(h, s, v) {
    let r, g, b, i, f, p, q, t;
    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);

    switch (i % 6) {
      case 0:
        (r = v), (g = t), (b = p);
        break;
      case 1:
        (r = q), (g = v), (b = p);
        break;
      case 2:
        (r = p), (g = v), (b = t);
        break;
      case 3:
        (r = p), (g = q), (b = v);
        break;
      case 4:
        (r = t), (g = p), (b = v);
        break;
      case 5:
        (r = v), (g = p), (b = q);
        break;
    }

    return { r, g, b };
  }

  wrap(value, min, max) {
    const range = max - min;
    if (range === 0) return min;
    return ((value - min) % range) + min;
  }

  getResolution(resolution) {
    const gl = this.gl;
    let aspectRatio = gl.drawingBufferWidth / gl.drawingBufferHeight;
    if (aspectRatio < 1) aspectRatio = 1.0 / aspectRatio;

    const min = Math.round(resolution);
    const max = Math.round(resolution * aspectRatio);

    if (gl.drawingBufferWidth > gl.drawingBufferHeight) {
      return { width: max, height: min };
    } else {
      return { width: min, height: max };
    }
  }

  scaleByPixelRatio(input) {
    const pixelRatio = window.devicePixelRatio || 1;
    return Math.floor(input * pixelRatio);
  }

  setupResizeHandler() {
    window.addEventListener("resize", this.throttledResize);
  }

  handleResize() {
    const wasLargeDevice = this.isLargeDevice;
    this.isLargeDevice = window.innerWidth >= 1200;

    // If device type changed, reinitialize or destroy
    if (wasLargeDevice !== this.isLargeDevice) {
      if (this.isLargeDevice) {
        // Device became large - initialize
        this.initFluid();
      } else {
        // Device became small - destroy
        this.destroy();
      }
    } else if (this.isLargeDevice && this.canvas) {
      // Just resize the canvas if it's still a large device
      this.resizeCanvas();
    }
  }

  destroy() {
    // Stop the animation loop
    this.isRunning = false;

    // Remove canvas from DOM
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }

    // Remove mask SVG
    const maskSVG = document.getElementById("fluid-mask");
    if (maskSVG && maskSVG.parentNode) {
      maskSVG.parentNode.removeChild(maskSVG);
    }

    // Clean up WebGL context
    if (this.gl) {
      const gl = this.gl;
      const ext = gl.getExtension("WEBGL_lose_context");
      if (ext) {
        ext.loseContext();
      }
    }

    // Reset properties
    this.canvas = null;
    this.gl = null;
    this.ext = null;
    this.programs = {};
    this.framebuffers = {};
    this.pointers = [];
  }
}

// Initialize fluid effect only on large viewports (>= 1199px)
// Matches cursor breakpoint to ensure consistent behavior
if (window.innerWidth >= 1199) {
  new SplashCursor();
}
